import { createSelector, Selector } from 'reselect';
import { AxiosError } from 'axios';
import { maxBy } from 'lodash';

import {
  ActionType,
  ChallengesType,
  ChallengeResultType,
  ChallengeRanksType,
  ResultPerProductType,
  LoadingStatusType,
  IChallengeList,
  IChallengeSummaryProduct,
  IChallengeListDeclarationProductItem,
} from 'interfaces';
import { FAILURE, SUCCESS } from 'services/api';
import {
  USER_CHALLENGES,
  SET_USER_CHALLENGES_LOADING_STATUS,
  USER_CHALLENGE,
  POST_USER_PARTICIPATION,
  SET_CHALLENGE_LOADING_STATUS,
  TOTAL_USER_PARTICIPATION,
  CHALLENGE_RESULTS,
  SET_CHALLENGE_RESULTS_STATUS,
  CHALLENGE_RESULT_VOTE,
  CHALLENGE_RESULT_UNVOTE,
  SET_CHALLENGE_RESULT_VOTING_STATUS,
  CHALLENGE_RANKS,
  SET_CHALLENGE_RANKS_LOADING,
  POST_USER_CHALLENGE_PHOTO_RESULTS,
  USER_CHALLENGE_DECLARATIONS,
  SET_SELECTED_CHALLENGE_DATA_ACTION,
  SET_LOADING_SELECTED_CHALLENGE_DATA_ACTION,
} from 'actions/challenge.actions';
import { normalize } from 'utils';
import { PRODUCTS } from 'actions/product.actions';
import { LOADING_STATUS } from 'constants/status';
import { GOAL, CHALLENGE } from 'constants/challenges';

import { userInfosSelector } from './app.reducer';

type ChallengeStateType = {
  status: LoadingStatusType;
  data: ChallengesType;
  error?: AxiosError;
};
export type ChallengeListStateType = {
  status: LoadingStatusType;
  data: IChallengeList;
  error?: AxiosError;
};
export type ChallengeResultsDataType = {
  allIds: number[];
  byId: { [resultId: string]: ChallengeResultType };
};
export type ChallengeResultsStateType = {
  status: LoadingStatusType;
  isFirstCall: boolean;
  data: ChallengeResultsDataType;
  page: number;
  totalItems: number | null;
  error?: AxiosError;
  isWaitingModeration: boolean;
};
type ChallengeResultVotingStateType = {
  status: LoadingStatusType;
  error?: AxiosError;
};
type ChallengeRanksStateType = {
  data: ChallengeRanksType;
  error?: AxiosError;
};
export type historySelectedChallengeType = {
  selectedChallengeId: string | undefined;
  isInDeclaredChallengesList: boolean;
  data: any;
};

export interface IChallengesState {
  userChallengesStatus: LoadingStatusType;
  userChallengesError: null | AxiosError;
  allIds: number[];
  byId: { [id: string]: ChallengeStateType };
  results: {
    allIds: number[];
    byId: { [id: string]: ChallengeResultsStateType };
    voting: { [resultId: string]: ChallengeResultVotingStateType };
  };
  ranks: {
    loading: boolean;
    allIds: number[];
    byId: { [id: string]: ChallengeRanksStateType };
  };
  historySelectedChallenge: historySelectedChallengeType | null | undefined;
  historySelectedChallengeLoadingStatus: LoadingStatusType;
}

type ChallengeSelector<T> = Selector<{ challenges: IChallengesState }, T>;

export const initialState: IChallengesState = {
  userChallengesStatus: LOADING_STATUS.IDLE,
  userChallengesError: null,
  allIds: [],
  byId: {},
  results: {
    allIds: [],
    byId: {},
    voting: {},
  },
  ranks: {
    loading: false, // TODO: change to statuses instead of 1 global loading (like results)
    allIds: [],
    byId: {},
  },
  historySelectedChallenge: undefined,
  historySelectedChallengeLoadingStatus: LOADING_STATUS.IDLE,
};

export const challengesReducerSelector = (state: {
  challenges: IChallengesState;
}): IChallengesState => state.challenges;

export const challengesStatusSelector = createSelector(
  challengesReducerSelector,
  ({ userChallengesStatus }: IChallengesState) => userChallengesStatus,
);

export const historySelectedChallengeSelector = createSelector(
  challengesReducerSelector,
  ({ historySelectedChallenge }: IChallengesState) => historySelectedChallenge,
);

export const historySelectedChallengeLoadingSelector = createSelector(
  challengesReducerSelector,
  ({ historySelectedChallengeLoadingStatus }: IChallengesState) =>
    historySelectedChallengeLoadingStatus,
);

export const challengesErrorSelector = createSelector(
  challengesReducerSelector,
  ({ userChallengesError }: IChallengesState) => userChallengesError,
);

export const challengesSelector = createSelector(
  challengesReducerSelector,
  (challenges: IChallengesState): ChallengeStateType[] =>
    Object.values(challenges.byId),
);

export const challengesListSelector = createSelector(
  challengesSelector,
  (challenges: ChallengeStateType[]): ChallengeListStateType[] =>
    challenges.filter(
      (challenge: ChallengeStateType) => challenge.data?.type === 'List',
    ) as ChallengeListStateType[],
);

export const challengesDeclaredListSelector = createSelector(
  challengesListSelector,
  (challenges: ChallengeListStateType[]): ChallengeListStateType[] =>
    challenges.filter(
      (challenge: ChallengeListStateType) =>
        challenge.data.totalDeclaration > 0,
    ),
);

export const challengesListDataSelector = createSelector(
  challengesListSelector,
  (challenges: ChallengeListStateType[]): IChallengeList[] =>
    challenges.map(({ data }: ChallengeListStateType) => data),
);

export const challengesDeclaredListDataSelector = createSelector(
  challengesDeclaredListSelector,
  (challenges: ChallengeListStateType[]): IChallengeList[] =>
    challenges.map(({ data }: ChallengeListStateType) => data),
);

export const sortedDeclaredChallengesSelector = createSelector(
  challengesDeclaredListDataSelector,
  (challenges: IChallengeList[]): IChallengeList[] => [...challenges].reverse(),
);

export const challengeSelector = (
  challengeId: number | string | undefined,
): ChallengeSelector<ChallengeStateType> =>
  createSelector(
    challengesReducerSelector,
    (challenges) => challenges.byId[challengeId!] || {},
  );

export const isRewardingAutoSelector = (challengeId: number) =>
  createSelector(
    challengeSelector(challengeId),
    ({ data }) => data.isRewardingAuto,
  );

export const challengeListUserResult = (challengeId: number) =>
  createSelector(
    userInfosSelector,
    challengeSelector(challengeId),
    (userInfo, { data }) => {
      if (!data) {
        return;
      }

      const {
        isRewardingGlobal,
        isRewardingAuto,
        challengeResults,
        allChallengeListUsersResult = [],
        goalType,
        type,
      } = data;

      if (
        type === CHALLENGE.LIST &&
        goalType === GOAL.RESULT &&
        !isRewardingGlobal &&
        isRewardingAuto
      ) {
        const resultIndex = allChallengeListUsersResult.findIndex(
          (result) => result.id === userInfo.id,
        );
        if (resultIndex !== -1) {
          const result = allChallengeListUsersResult[resultIndex];
          return {
            resultValue: result.totalPoints,
            rank: resultIndex + 1,
          };
        }
      }

      // Get the best result in case of multi results
      return maxBy(challengeResults, 'resultValue');
    },
  );

export const isChallengeListResultPerProduct = (challengeId: number) =>
  createSelector(
    challengeSelector(challengeId),
    ({ data }) =>
      data.type === CHALLENGE.LIST &&
      data.goalType === GOAL.RESULT &&
      !data.isRewardingGlobal,
  );

export const challengeListUsersResult = (
  challengeId: number,
): ChallengeSelector<(ChallengeResultType | ResultPerProductType)[]> =>
  createSelector(
    challengeSelector(challengeId),
    isChallengeListResultPerProduct(challengeId),
    challengeResultsSelector(challengeId),
    ({ data }, isResultPerProduct, challengeResults) => {
      const { allChallengeListUsersResult = [] } = data;
      return isResultPerProduct
        ? allChallengeListUsersResult
          .sort((a, b) => b.totalPoints - a.totalPoints) // Trier par totalPoints décroissant
          .reduce((acc, result, key) => {
            const previewsResult = acc[key - 1];
            const resultValue = result.totalPoints;
            const rank =
                key !== 0 && resultValue === previewsResult?.resultValue
                  ? previewsResult.rank
                  : key + 1;
            acc.push({
              entity: {
                userId: result.id,
                profile: {
                  firstName: result.firstName,
                  lastName: result.lastName,
                },
              },
              resultValue,
              rank,
            });
            return acc;
          }, [] as ResultPerProductType[])
        : Object.values(challengeResults.data?.byId ?? {}).filter(
          (result) => result.resultValue != null,
        );
    },
  );

export const challengeResultsSelector = (
  challengeId: number,
): ChallengeSelector<ChallengeResultsStateType> =>
  createSelector(
    challengesReducerSelector,
    (challenges) => challenges.results.byId[challengeId] || {},
  );

export const challengeResultVotingSelector = (
  resultId: number,
): ChallengeSelector<ChallengeResultVotingStateType> =>
  createSelector(
    challengesReducerSelector,
    (challenges) => challenges.results.voting[resultId] || {},
  );

export const challengeRanksSelector = (
  challengeId: number,
): ChallengeSelector<ChallengeRanksStateType> =>
  createSelector(
    challengesReducerSelector,
    (challenges) => challenges.ranks.byId[challengeId] || {},
  );

export const challengesReducer = (
  state: IChallengesState = initialState,
  action: ActionType,
): IChallengesState => {
  const { payload } = action;

  switch (action.type) {
    case SET_CHALLENGE_LOADING_STATUS:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            status: payload.status,
          },
        },
      };
    case USER_CHALLENGE[SUCCESS]:
      return {
        ...state,
        allIds: state.allIds.includes(payload.challengeId)
          ? state.allIds
          : [...state.allIds, payload.challengeId],
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: null,
            data: {
              ...payload.data,
              totalParticipatingUsers:
                state.byId[payload.challengeId]?.data?.totalParticipatingUsers,
              declarations: state.byId[payload.challengeId]?.data?.declarations,
            },
          },
        },
      };
    case USER_CHALLENGE[FAILURE]:
      return {
        ...state,
        allIds: state.allIds.includes(payload.challengeId)
          ? state.allIds
          : [...state.allIds, payload.challengeId],
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: payload.error,
          },
        },
      };
    case USER_CHALLENGE_DECLARATIONS[SUCCESS]:
      return {
        ...state,
        allIds: state.allIds.includes(payload.challengeId)
          ? state.allIds
          : [...state.allIds, payload.challengeId],
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            data: {
              ...state.byId[payload.challengeId].data,
              declarations: payload.data.map((declaration: any) => ({
                ...declaration,
                challengeProductDeclarations:
                  declaration.challengeProductDeclarations.map(
                    (
                      product: IChallengeListDeclarationProductItem,
                    ): IChallengeSummaryProduct => ({
                      ...product.challengeProduct,
                      reward: product.reward,
                      quantity: product.quantity,
                      value: product.currencyValue || 0,
                      productRewardValue: product.productRewardValue || 0,
                      productResultValue: product.productResultValue || 0,
                      threshold: product.threshold || 0,
                      coefficient: product.coefficient || 0,
                    }),
                  ),
              })),
            },
          },
        },
      };
    case USER_CHALLENGES[SUCCESS]:
      return {
        ...state,
        allIds: payload.data.reduce(
          (acc: number[], { id }: { id: number }) =>
            state.allIds.includes(id) ? acc : [...acc, id],
          state.allIds,
        ),
        byId: payload.data.reduce(
          (acc: IChallengeList, action: IChallengeList) => ({
            ...acc,
            [action.id]: {
              ...state.byId?.[action.id],
              error: null,
              status: LOADING_STATUS.IDLE,
              data: {
                ...state.byId?.[action.id]?.data,
                ...action,
                type: 'List',
              },
            },
          }),
          state.byId,
        ),
      };
    case SET_USER_CHALLENGES_LOADING_STATUS:
      return {
        ...state,
        userChallengesStatus: payload.status,
      };
    case SET_SELECTED_CHALLENGE_DATA_ACTION[SUCCESS]:
      return {
        ...state,
        historySelectedChallenge: {
          selectedChallengeId:
            payload.historySelectedChallenge.selectedChallengeId,
          isInDeclaredChallengesList:
            payload.historySelectedChallenge.isInDeclaredChallengesList,
          data: payload.historySelectedChallenge.data,
        },
      };
    case SET_LOADING_SELECTED_CHALLENGE_DATA_ACTION:
      return {
        ...state,
        historySelectedChallengeLoadingStatus: payload.status,
      };
    case USER_CHALLENGES[FAILURE]:
      return {
        ...state,
        userChallengesError: payload.error,
      };
    case POST_USER_PARTICIPATION[SUCCESS]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: null,
            data: {
              ...state.byId[payload.challengeId].data,
              challengeResults: [payload.data],
            },
          },
        },
      };
    case POST_USER_PARTICIPATION[FAILURE]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: payload.error,
          },
        },
      };
    case TOTAL_USER_PARTICIPATION[SUCCESS]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: null,
            data: {
              ...state.byId[payload.challengeId].data,
              totalParticipatingUsers: payload.totalParticipatingUsers,
            },
          },
        },
      };
    case TOTAL_USER_PARTICIPATION[FAILURE]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: payload.error,
          },
        },
      };
    case SET_CHALLENGE_RESULTS_STATUS:
      return {
        ...state,
        results: {
          ...state.results,
          byId: {
            ...state.results.byId,
            [payload.challengeId]: {
              ...state.results.byId[payload.challengeId],
              status: payload.status,
              isFirstCall:
                payload.isFirstCall === null
                  ? state.results.byId[payload.challengeId].isFirstCall
                  : payload.isFirstCall,
            },
          },
        },
      };
    case CHALLENGE_RESULTS[SUCCESS]:
      return {
        ...state,
        results: {
          ...state.results,
          allIds: state.results.allIds.includes(payload.challengeId)
            ? state.results.allIds
            : [...state.results.allIds, payload.challengeId],
          byId: {
            ...state.results.byId,
            [payload.challengeId]: {
              ...state.results.byId[payload.challengeId],
              data:
                payload.page === 1
                  ? normalize(payload.data)
                  : {
                    ...state.results.byId[payload.challengeId].data,
                    allIds: [
                      ...state.results.byId[payload.challengeId].data.allIds,
                      ...payload.data.map(
                        (result: ChallengeResultType) => result.id,
                      ),
                    ],
                    byId: payload.data.reduce(
                      (
                        acc: { [id: string]: ChallengeResultType },
                        curr: ChallengeResultType,
                      ) => {
                        acc[curr.id] = curr;
                        return acc;
                      },
                      state.results.byId[payload.challengeId].data.byId,
                    ),
                  },
              page: payload.page,
              totalItems: payload.totalItems,
              error: null,
              isWaitingModeration: payload.isWaitingModeration,
            },
          },
        },
      };
    case CHALLENGE_RESULTS[FAILURE]:
      return {
        ...state,
        results: {
          ...state.results,
          byId: {
            ...state.results.byId,
            [payload.challengeId]: {
              ...state.results.byId[payload.challengeId],
              error: payload.error,
            },
          },
        },
      };
    case SET_CHALLENGE_RESULT_VOTING_STATUS:
      return {
        ...state,
        results: {
          ...state.results,
          voting: {
            ...state.results.voting,
            [payload.resultId]: {
              ...state.results.voting[payload.resultId],
              status: payload.status,
            },
          },
        },
      };
    case CHALLENGE_RESULT_VOTE[SUCCESS]:
    case CHALLENGE_RESULT_UNVOTE[SUCCESS]:
      return {
        ...state,
        results: {
          ...state.results,
          voting: {
            ...state.results.voting,
            [payload.resultId]: {
              ...state.results.voting[payload.resultId],
              error: null,
            },
          },
          byId: state.results.allIds.reduce(
            (
              acc: { [id: string]: ChallengeResultsStateType },
              curr: number,
            ) => {
              acc[curr] = {
                ...state.results.byId[curr],
                data: {
                  allIds: [...state.results.byId[curr].data.allIds],
                  byId: {
                    ...state.results.byId[curr].data.byId,
                    [payload.resultId]: {
                      ...state.results.byId[curr].data.byId[payload.resultId],
                      ...payload.data,
                    },
                  },
                },
              };
              return acc;
            },
            {},
          ),
        },
      };
    case CHALLENGE_RESULT_VOTE[FAILURE]:
    case CHALLENGE_RESULT_UNVOTE[FAILURE]:
      return {
        ...state,
        results: {
          ...state.results,
          voting: {
            ...state.results.voting,
            [payload.resultId]: {
              ...state.results.voting[payload.resultId],
              error: payload.error,
            },
          },
        },
      };
    case SET_CHALLENGE_RANKS_LOADING:
      return {
        ...state,
        ranks: {
          ...state.ranks,
          loading: payload.isLoading,
        },
      };
    case CHALLENGE_RANKS[SUCCESS]:
      return {
        ...state,
        ranks: {
          ...state.ranks,
          allIds: state.ranks.allIds.includes(payload.challengeId)
            ? state.ranks.allIds
            : [...state.ranks.allIds, payload.challengeId],
          byId: {
            ...state.ranks.byId,
            [payload.challengeId]: {
              ...state.ranks.byId[payload.challengeId],
              error: null,
              data: payload.data,
            },
          },
        },
      };
    case CHALLENGE_RANKS[FAILURE]:
      return {
        ...state,
        ranks: {
          ...state.ranks,
          byId: {
            ...state.ranks.byId,
            [payload.challengeId]: {
              ...state.ranks.byId[payload.challengeId],
              error: payload.error,
              data: {},
            },
          },
        },
      };
    case POST_USER_CHALLENGE_PHOTO_RESULTS[SUCCESS]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            data: {
              ...state.byId[payload.challengeId].data,
              challengeResults: [payload.data],
            },
          },
        },
      };
    case POST_USER_CHALLENGE_PHOTO_RESULTS[FAILURE]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: payload.error,
          },
        },
      };
    // Specific api call challenge-list-product override this storred product state
    case PRODUCTS[SUCCESS]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: null,
            data: {
              ...state.byId[payload.challengeId].data,
              challengeListProducts: [...payload.data['hydra:member']],
            },
          },
        },
      };
    case PRODUCTS[FAILURE]:
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.challengeId]: {
            ...state.byId[payload.challengeId],
            error: payload.error,
            data: {
              ...state.byId[payload.challengeId].data,
              challengeListProducts: [...payload.data['hydra:member']],
            },
          },
        },
      };

    default: {
      return state;
    }
  }
};

export default challengesReducer;
